/**
 * GoalsPage — cele oszczędnościowe.
 * Lista kart z progress bar + button "Wpłać" / "Edytuj". FAB → nowy cel.
 */
import { useState } from 'react';
import { Icons } from '../components/Icons';
import { useGoalsStore, goalSaved, goalProgress, type Goal } from '../features/goals/goalsStore';
import { formatCurrency, safeColor, formatDate } from '../lib/utils';
import { getCategoryIcon } from '../lib/categoryIcons';
import { GoalModal } from '../components/goals/GoalModal';
import { DepositModal } from '../components/goals/DepositModal';

export function GoalsPage() {
  const goals = useGoalsStore((s) => s.goals);
  const [editingGoal, setEditingGoal] = useState<Goal | 'new' | null>(null);
  const [depositTarget, setDepositTarget] = useState<Goal | null>(null);

  const totalSaved = goals.reduce((s, g) => s + goalSaved(g), 0);
  const totalTarget = goals.reduce((s, g) => s + g.target, 0);
  const overallPct = totalTarget > 0 ? Math.round((totalSaved / totalTarget) * 100) : 0;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-display font-bold text-slate-900 dark:text-white">Cele</h1>
        <button
          type="button"
          onClick={() => setEditingGoal('new')}
          className="px-3 py-2 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold flex items-center gap-1.5 transition-colors shadow-card"
        >
          <Icons.Plus size={16} strokeWidth={2.5} />
          Nowy
        </button>
      </div>

      {goals.length > 0 && (
        <div className="bg-gradient-to-br from-sky-500 to-emerald-500 rounded-card shadow-card p-5 text-white">
          <p className="text-xs uppercase tracking-wide font-bold opacity-80">Łącznie odłożone</p>
          <p className="text-3xl font-bold tabular-nums mt-1">{formatCurrency(totalSaved)}</p>
          <p className="text-sm opacity-90 mt-1">
            {overallPct}% z {formatCurrency(totalTarget)}
          </p>
          <div className="h-2 mt-3 rounded-full bg-white/25 overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all duration-700"
              style={{ width: `${overallPct}%` }}
            />
          </div>
        </div>
      )}

      {goals.length === 0 ? (
        <div className="text-center py-12">
          <Icons.Goals size={48} className="mx-auto text-slate-300 dark:text-slate-600 mb-3" />
          <p className="text-slate-500 dark:text-slate-400 mb-1">Brak celów</p>
          <p className="text-sm text-slate-400 dark:text-slate-500">
            Utwórz pierwszy cel oszczędnościowy
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {goals.map((g) => {
            const Icon = getCategoryIcon(g.icon);
            const color = safeColor(g.color, '#0ea5e9');
            const saved = goalSaved(g);
            const pct = goalProgress(g);
            return (
              <div key={g.id} className="bg-white dark:bg-slate-800 rounded-card border border-slate-100 dark:border-slate-700 shadow-card p-4">
                <div className="flex items-start gap-3">
                  <span
                    className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: `${color}22`, color }}
                  >
                    <Icon size={22} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{g.name}</p>
                      <span className="text-xs font-bold text-slate-500 dark:text-slate-400 tabular-nums">{pct}%</span>
                    </div>
                    {g.deadline && (
                      <p className="text-[11px] text-slate-400 mt-0.5">do {formatDate(g.deadline)}</p>
                    )}
                    <div className="h-2 mt-2 rounded-full bg-slate-100 dark:bg-slate-700 overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${pct}%`, backgroundColor: color }}
                      />
                    </div>
                    <div className="flex items-baseline justify-between mt-2 text-sm">
                      <span className="text-emerald-600 dark:text-emerald-300 font-bold tabular-nums">
                        {formatCurrency(saved)}
                      </span>
                      <span className="text-slate-400 tabular-nums">/ {formatCurrency(g.target)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    type="button"
                    onClick={() => setDepositTarget(g)}
                    className="flex-1 px-3 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
                  >
                    Wpłać
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingGoal(g)}
                    className="px-3 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 text-sm font-semibold hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
                  >
                    <Icons.Edit size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editingGoal && (
        <GoalModal
          goal={editingGoal === 'new' ? null : editingGoal}
          onClose={() => setEditingGoal(null)}
        />
      )}
      {depositTarget && (
        <DepositModal goal={depositTarget} onClose={() => setDepositTarget(null)} />
      )}
    </div>
  );
}
